import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Button } from "../ui/button";
import { toast } from "sonner";

const SLOT_LABELS = { ancestry_feat: "Ancestry Feat", class_feat: "Class Feat", skill_feat: "Skill Feat", general_feat: "General Feat", skill_increase: "Skill Increase" };
const FEAT_TYPE_FOR_SLOT = { ancestry_feat: "ancestry", class_feat: "class", skill_feat: "skill", general_feat: "general" };
const SKILLS = ["acrobatics", "arcana", "athletics", "crafting", "deception", "diplomacy", "intimidation", "medicine", "nature", "occultism", "performance", "religion", "society", "stealth", "survival", "thievery"];
const RANK_ORDER = ["untrained", "trained", "expert", "master", "legendary"];

// Auto-Leveling Engine: surfaces the PF2e feat/skill-increase choices a level-up just unlocked
// (HP was already auto-applied server-side) — the player picks, we do the math.
export default function PendingLevelSlotsPanel({ character, feats, patchChar }) {
  const [picks, setPicks] = useState({});
  const slots = character.pending_level_slots || [];
  if (slots.length === 0) return null;

  const resolveSlot = async (idx) => {
    const slot = slots[idx];
    const value = picks[idx];
    if (!value) return toast.error("Pick something first");
    const remaining = slots.filter((_, i) => i !== idx);
    if (slot.slot_type === "skill_increase") {
      const currentRank = character.skill_proficiencies?.[value] || "untrained";
      const nextRank = RANK_ORDER[Math.min(RANK_ORDER.length - 1, RANK_ORDER.indexOf(currentRank) + 1)];
      await patchChar({ skill_proficiencies: { ...character.skill_proficiencies, [value]: nextRank }, pending_level_slots: remaining });
      toast.success(`${value} increased to ${nextRank}!`);
    } else {
      const feat = feats.find((f) => f.slug === value);
      await patchChar({ selected_feat_slugs: [...(character.selected_feat_slugs || []), value], pending_level_slots: remaining });
      toast.success(`${feat?.name || "Feat"} added!`);
    }
    setPicks((p) => { const n = { ...p }; delete n[idx]; return n; });
  };

  return (
    <div className="rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-4 space-y-2" data-testid="pending-level-slots-panel">
      <p className="text-xs uppercase tracking-widest text-[#D4AF37]">Level-Up Choices Pending ({slots.length})</p>
      {slots.map((slot, idx) => (
        <div key={`${slot.level}-${slot.slot_type}-${idx}`} className="flex items-center gap-2 bg-black/20 rounded-lg px-3 py-2" data-testid={`pending-slot-${slot.level}-${slot.slot_type}`}>
          <span className="text-xs text-[#f1e0c5]/60 w-32 shrink-0">Lv{slot.level} — {SLOT_LABELS[slot.slot_type]}</span>
          <Select value={picks[idx] || ""} onValueChange={(v) => setPicks((p) => ({ ...p, [idx]: v }))}>
            <SelectTrigger data-testid={`pending-slot-select-${idx}`} className="flex-1 h-8 bg-black/30 border-white/10 text-xs">
              <SelectValue placeholder={slot.slot_type === "skill_increase" ? "Pick a skill…" : "Pick a feat…"} />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {slot.slot_type === "skill_increase"
                ? SKILLS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)
                : feats.filter((f) => f.type === FEAT_TYPE_FOR_SLOT[slot.slot_type] && !(character.selected_feat_slugs || []).includes(f.slug))
                    .map((f) => <SelectItem key={f.slug} value={f.slug}>{f.name} (Lv{f.level})</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid={`pending-slot-confirm-${idx}`} size="sm" className="rounded-full bg-[#D90429] hover:bg-[#b8031f] h-8" onClick={() => resolveSlot(idx)}>Confirm</Button>
        </div>
      ))}
    </div>
  );
}
